<template>
  <div class="editor">
    <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
      <div class="menu-bar-inner">
        <v-btn text :color="isActive.bold() ? 'primary': '#7C7C7C'" @click="commands.bold" small>
          <v-icon>mdi-format-bold</v-icon>
        </v-btn>
        <v-btn
          text
          :color="isActive.italic() ? 'primary': '#7C7C7C'"
          @click="commands.italic"
          small
        >
          <v-icon>mdi-format-italic</v-icon>
        </v-btn>
        <v-btn
          text
          :color="isActive.strike() ? 'primary': '#7C7C7C'"
          @click="commands.strike"
          small
        >
          <v-icon>mdi-format-strikethrough</v-icon>
        </v-btn>
        <v-btn
          text
          :color="isActive.underline() ? 'primary': '#7C7C7C'"
          @click="commands.underline"
          small
        >
          <v-icon>mdi-format-underline</v-icon>
        </v-btn>
        <v-btn text @click="showImagePicker(commands.image)" small color="#7C7C7C">
          <v-icon>mdi-image</v-icon>
        </v-btn>
        <v-divider vertical></v-divider>
        <v-btn
          :color="isActive.heading({ level: 1 }) ? 'primary': '#7C7C7C'"
          @click="commands.heading({ level: 1 })"
          small
          text
        >H1</v-btn>
        <v-btn
          :color="isActive.heading({ level: 2 }) ? 'primary': '#7C7C7C'"
          @click="commands.heading({ level: 2 })"
          small
          text
        >H2</v-btn>
        <v-btn
          :color="isActive.heading({ level: 3 }) ? 'primary': '#7C7C7C'"
          @click="commands.heading({ level: 3 })"
          small
          text
        >H3</v-btn>
        <v-divider vertical></v-divider>
        <v-btn
          text
          :color="isActive.bullet_list() ? 'primary': '#7C7C7C'"
          @click="commands.bullet_list"
          small
        >
          <v-icon>mdi-format-list-bulleted</v-icon>
        </v-btn>
        <v-btn
          text
          :color="isActive.ordered_list() ? 'primary': '#7C7C7C'"
          @click="commands.ordered_list"
          small
        >
          <v-icon>mdi-format-list-numbered</v-icon>
        </v-btn>
        <v-divider vertical></v-divider>
        <v-btn
          text
          :color="isActive.blockquote() ? 'primary': '#7C7C7C'"
          @click="commands.blockquote"
          small
        >
          <v-icon>mdi-format-quote-close</v-icon>
        </v-btn>
        <v-btn
          text
          :color="isActive.code_block() ? 'primary': '#7C7C7C'"
          @click="commands.code_block"
          small
        >
          <v-icon>mdi-code-tags</v-icon>
        </v-btn>
        <v-btn text @click="commands.undo" small color="#7C7C7C">
          <v-icon>mdi-undo</v-icon>
        </v-btn>
        <v-btn text @click="commands.redo" small color="#7C7C7C">
          <v-icon>mdi-redo</v-icon>
        </v-btn>
      </div>
    </editor-menu-bar>
    <editor-menu-bubble
      :editor="editor"
      :keep-in-bounds="true"
      v-slot="{ commands, isActive, menu, getMarkAttrs }"
    >
      <div
        class="menububble"
        :class="{ 'is-active': menu.isActive }"
        :style="`left: ${menu.left}px; bottom: ${menu.bottom}px;`"
      >
        <form
          class="menububble__form"
          v-if="linkMenuIsActive"
          @submit.prevent="setLinkUrl(commands.link, linkUrl)"
        >
          <input
            class="menububble__input"
            type="text"
            v-model="linkUrl"
            placeholder="https://"
            ref="linkInput"
            @keydown.esc="hideLinkMenu"
          />
          <v-btn @click="setLinkUrl(commands.link, null)" type="button" color="white" text small>
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </form>

        <template v-else>
          <v-btn
            @click="showLinkMenu(getMarkAttrs('link'))"
            :color="isActive.blockquote() ? 'primary': '#FFFFFF'"
            text
            small
          >
            <span>{{ isActive.link() ? 'Update Link' : 'Add Link'}}</span>
            <v-icon>mdi-link-variant</v-icon>
          </v-btn>
        </template>
      </div>
    </editor-menu-bubble>
    <v-divider></v-divider>
    <v-card elevation="1" class="pa-5" style="margin-top: 50px;">
      <editor-content class="editor__content" :editor="editor" style="min-height: 400px;" />
    </v-card>
    <v-dialog v-model="isShowImagePicker" width="300px" height="100px">
      <v-card>
        <v-card-title class="grey lighten-2" primary-title>Insert image</v-card-title>
        <ImagePickerUpload @input="onImageUploaded($event)"></ImagePickerUpload>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar, EditorMenuBubble } from "tiptap";
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  OrderedList,
  BulletList,
  Bold,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Image,
  ListItem,
  TodoItem,
  TodoList
} from "tiptap-extensions";
import ImagePickerUpload from "./ImagePickerUpload";

export default {
  // eslint-disable-next-line vue/no-unused-components
  components: {
    EditorMenuBar,
    EditorContent,
    ImagePickerUpload,
    EditorMenuBubble
  },
  props: ["content"],
  data() {
    return {
      dialog: false,
      editor: null,
      isShowImagePicker: false,
      imageCommand: null,
      contentCloned: "",
      linkUrl: null,
      linkMenuIsActive: false
    };
  },
  mounted() {
    this.editor = new Editor({
      extensions: [
        new Blockquote(),
        new CodeBlock(),
        new HardBreak(),
        new Heading({ levels: [1, 2, 3] }),
        new BulletList(),
        new OrderedList(),
        new Bold(),
        new Italic(),
        new Link(),
        new Strike(),
        new Underline(),
        new History(),
        new Image(),
        new ListItem(),
        new TodoItem(),
        new TodoList()
      ],
      content: this.contentCloned,
      onUpdate: () => {
        this.$emit("input", this.getHTML());
      }
    });
  },
  created() {
    this.contentCloned = this.content;
  },
  beforeDestroy() {
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    showImagePicker(command) {
      this.isShowImagePicker = true;
      this.imageCommand = command;
    },
    showLinkMenu(attrs) {
      this.linkUrl = attrs.href;
      this.linkMenuIsActive = true;
      this.$nextTick(() => {
        this.$refs.linkInput.focus();
      });
    },
    hideLinkMenu() {
      this.linkUrl = null;
      this.linkMenuIsActive = false;
    },
    setLinkUrl(command, url) {
      command({ href: url });
      this.hideLinkMenu();
    },
    onImageUploaded(url) {
      this.imageCommand({ src: url });
      this.isShowImagePicker = false;
      this.imageCommand = null;
    },
    getHTML() {
      return this.editor.getHTML();
    }
  }
};
</script>

<style>
.menu-bar-inner {
  padding-top: 8px;
  padding-bottom: 8px;
  display: flex;
  justify-content: space-between;
  position: sticky;
  top: 48px;
  z-index: 1000;
  background: white;
  border-bottom: 1px solid #efefef;
}

.editor {
  position: relative;
  margin: 0 auto 5rem auto;
}
.editor__content {
  overflow-wrap: break-word;
  word-wrap: break-word;
  word-break: break-word;
  height: 100%;
  width: 100%;
}
.editor__content * {
  caret-color: currentColor;
}
.editor__content pre {
  padding: 0.7rem 1rem;
  border-radius: 5px;
  background: black;
  color: white;
  font-size: 0.8rem;
  overflow-x: auto;
}
.editor__content pre code {
  display: block;
}
.editor__content p code {
  display: inline-block;
  padding: 0 0.4rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: bold;
  background: rgba(0, 0, 0, 0.1);
  color: rgba(255, 255, 255, 0.8);
}
.editor__content ul,
.editor__content ol {
  padding-left: 1rem;
}
.editor__content li > p,
.editor__content li > ol,
.editor__content li > ul {
  margin: 0;
}
.editor__content a {
  color: inherit;
}
.editor__content blockquote {
  border-left: 3px solid rgba(0, 0, 0, 0.1);
  color: rgba(0, 0, 0, 0.8);
  padding-left: 0.8rem;
  font-style: italic;
}
.editor__content blockquote p {
  margin: 0;
}
.editor__content img {
  max-width: 100%;
  border-radius: 3px;
}
.editor__content table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  overflow: hidden;
}
.editor__content table td,
.editor__content table th {
  min-width: 1em;
  border: 2px solid #cccccc;
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}
.editor__content table td > *,
.editor__content table th > * {
  margin-bottom: 0;
}
.editor__content table th {
  font-weight: bold;
  text-align: left;
}
.editor__content table .selectedCell:after {
  z-index: 2;
  position: absolute;
  content: "";
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;
  background: rgba(200, 200, 255, 0.4);
  pointer-events: none;
}
.editor__content table .column-resize-handle {
  position: absolute;
  right: -2px;
  top: 0;
  bottom: 0;
  width: 4px;
  z-index: 20;
  background-color: #adf;
  pointer-events: none;
}
.editor__content .tableWrapper {
  margin: 1em 0;
  overflow-x: auto;
}
.editor__content .resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}

.ProseMirror {
  min-height: 100px;
}

.ProseMirror-focused {
  border: none;
  outline: none;
}

.menububble {
  position: absolute;
  display: flex;
  z-index: 20;
  background: black;
  border-radius: 5px;
  padding: 0.3rem;
  margin-bottom: 0.5rem;
  transform: translateX(-50%);
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s, visibility 0.2s;
}
.menububble.is-active {
  opacity: 1;
  visibility: visible;
}

.menububble__form {
  display: flex;
  align-items: center;
}
.menububble__input {
  font: inherit;
  border: none;
  background: transparent;
  color: white;
}
</style>